import React, {
	useRef,
} from 'react';

import {
	useDrag,
	useDrop,
} from 'react-dnd';

import CustomIcon from '../Common/CustomIcon/CustomIcon';

import {
	hasGlobalScope,
	hasPermission,
} from 'Vulture/Permissions.js';

// Dragging a system or group moves it between accounts/groups, which the backend gates on
// scope:global + system_edit (HandleSystemMove / HandleSystemGroupMove). Mirror that exactly
// here so the UI never offers a drag the API will reject — and so a custom role holding both
// gets the affordance, which the old `userRole === 'internal_admin'` test denied it.
const canMoveSystems = () => hasGlobalScope() && hasPermission('system_edit');

// Drag and drop types
export const ItemTypes = {
	SYSTEM:       'system',
	SYSTEM_GROUP: 'system_group',
};

// ============================================
// DraggableSystemRow - Draggable but NOT a drop target
// Systems can be dragged into system_groups but cannot contain other items
// ============================================

export const DraggableSystemRow = ({
	row,
	index,
	style,
	onMoveSystem,
	children,
	systemGroupUUID, // The system_group this system currently belongs to
	enableDragDrop = false, // call-site opt-in; see canMoveSystems above
}) => {
	const ref = useRef(null);
	const canDrag = enableDragDrop && canMoveSystems();

	const [
		{
			isDragging,
		},
		drag,
		preview,
	] = useDrag({
		type:    ItemTypes.SYSTEM,
		item:    () => {
			return {
				id:                     row.original.id,
				index,
				type:                   ItemTypes.SYSTEM,
				sourceSystemGroupUUID:  systemGroupUUID,
			};
		},
		canDrag: () => {
			return canDrag;
		},
		collect: (monitor) => {
			return {
				isDragging: monitor.isDragging(),
			};
		},
	});

	// Connect the preview to the whole ref
	preview(ref);

	const INDENT_STEP = 16;
	const depth = Number(row?.depth || 0);
	const handleIndent = `${depth * INDENT_STEP}px`;

	return (
		<div
			ref={ref}
			className={`${isDragging ? style.dragging : ''}`}
			style={{
				opacity: isDragging ? 0.5 : 1,
			}}
		>
			<div style={{
				display: 'flex',
				alignItems: 'center',
			}}>
				{canDrag && (
					<div
						ref={drag}
						className={style.dragHandle}
						data-test-id={`systemDragHandle_${row.original.id}`}
						title="Drag to move system"
						style={{ marginLeft: handleIndent }}
					>
						<CustomIcon icon="grip-vertical" color="#999" />
					</div>
				)}
				<div style={{
					flex: 1,
				}}>
					{children}
				</div>
			</div>
		</div>
	);
};

// ============================================
// DraggableSystemGroupRow - Both draggable AND a drop target
// System groups can be dragged and can accept systems and other system_groups
// ============================================

export const DraggableSystemGroupRow = ({
	row,
	index,
	style,
	onMoveSystem,
	onMoveSystemGroup,
	children,
	systemGroupUUID, // This system_group's UUID
	parentSystemGroupUUID, // The parent system_group's UUID (null if at root)
	accountUUID,
	openConfirmModal,
	enableDragDrop = false, // call-site opt-in; see canMoveSystems above
}) => {
	const ref = useRef(null);
	const canDragSystems = enableDragDrop && canMoveSystems();
	const canDragSystemGroups = enableDragDrop && canMoveSystems();

	const [
		{
			isOver,
			canDrop,
		},
		drop,
	] = useDrop({
		accept: [
			ItemTypes.SYSTEM,
			ItemTypes.SYSTEM_GROUP,
		],
		canDrop: (item, monitor) => {
			// Systems can be dropped into system_groups (different from source)
			if (item.type === ItemTypes.SYSTEM) {
				return canDragSystems && item.sourceSystemGroupUUID !== systemGroupUUID;
			}
			// System groups cannot be dropped into themselves or their descendants
			if (item.type === ItemTypes.SYSTEM_GROUP) {
				if (item.id === row.original.id) {
					return false;
				}
				// Don't allow dropping into itself
				return canDragSystemGroups;
			}
			return false;
		},
		drop: (item, monitor) => {
			if (!monitor.didDrop()) {
				if (item.type === ItemTypes.SYSTEM) {
					onMoveSystem(item.id, item.sourceSystemGroupUUID, systemGroupUUID);
				} else if (item.type === ItemTypes.SYSTEM_GROUP) {
					// For system group moves, show confirmation dialog
					if (openConfirmModal) {
						openConfirmModal(item.id, systemGroupUUID);
					} else {
						onMoveSystemGroup(item.id, systemGroupUUID, accountUUID);
					}
				}
			}
		},
		collect: (monitor) => {
			return {
				isOver:  monitor.isOver({
					shallow: true,
				}),
				canDrop: monitor.canDrop(),
			};
		},
	});

	const [
		{
			isDragging,
		},
		drag,
		preview,
	] = useDrag({
		type:    ItemTypes.SYSTEM_GROUP,
		item:    () => {
			return {
				id:                    row.original.id,
				index,
				type:                  ItemTypes.SYSTEM_GROUP,
				parentSystemGroupUUID,
			};
		},
		canDrag: () => {
			return canDragSystemGroups;
		},
		collect: (monitor) => {
			return {
				isDragging: monitor.isDragging(),
			};
		},
	});

	// Connect both drag and drop to the ref
	preview(drop(ref));

	const INDENT_STEP = 16;
	const depth = Number(row?.depth || 0);
	const handleIndent = `${depth * INDENT_STEP}px`;

	// Determine the appropriate visual feedback class
	let dropTargetClass = '';
	if (isOver) {
		if (canDrop) {
			dropTargetClass = style.dropTarget;
		} else {
			dropTargetClass = style.dropTargetInvalid;
		}
	}

	return (
		<div
			ref={ref}
			className={`${isDragging ? style.dragging : ''} ${dropTargetClass}`}
			style={{
				opacity: isDragging ? 0.5 : 1,
			}}
		>
			<div style={{
				display: 'flex',
				alignItems: 'center',
			}}>
				{canDragSystemGroups && (
					<div
						ref={drag}
						className={style.dragHandle}
						data-test-id={`hierarchyDragHandle_${row.original.id}`}
						title="Drag to move hierarchy"
						style={{ marginLeft: handleIndent }}
					>
						<CustomIcon icon="grip-vertical" color="#999" />
					</div>
				)}
				<div style={{
					flex: 1,
				}}>
					{children}
				</div>
			</div>
		</div>
	);
};

// ============================================
// AccountDropZone - Drop target for moving system_groups to account root
// ============================================

export const AccountDropZone = ({
	style,
	accountUUID,
	onMoveSystemGroup,
	openConfirmModal,
	enableDragDrop = false, // call-site opt-in; see canMoveSystems above
}) => {
	const ref = useRef(null);
	const canAccept = enableDragDrop && canMoveSystems();

	const [
		{
			isOver,
			canDrop,
		},
		drop,
	] = useDrop({
		accept: ItemTypes.SYSTEM_GROUP,
		canDrop: (item) => {
			// Only accept system groups that are not already at root level
			return canAccept && item.parentSystemGroupUUID !== null;
		},
		drop: (item, monitor) => {
			if (!monitor.didDrop()) {
				// null as target means move to root
				if (openConfirmModal) {
					openConfirmModal(item.id, null);
				} else {
					onMoveSystemGroup(item.id, null, accountUUID);
				}
			}
		},
		collect: (monitor) => {
			return {
				isOver:  monitor.isOver({
					shallow: true,
				}),
				canDrop: monitor.canDrop(),
			};
		},
	});

	drop(ref);

	if (!canAccept) {
		return null;
	}

	let dropZoneClass = style.accountDropZone;
	if (isOver) {
		if (canDrop) {
			dropZoneClass = `${style.accountDropZone} ${style.dropTarget}`;
		} else {
			dropZoneClass = `${style.accountDropZone} ${style.dropTargetInvalid}`;
		}
	}

	return (
		<div ref={ref} className={dropZoneClass}>
			<CustomIcon icon="level-up-alt" color="#999" />
			<span>Drop here to move system groups to account root level (not for individual systems)</span>
		</div>
	);
};

export default {
	ItemTypes,
	DraggableSystemRow,
	DraggableSystemGroupRow,
	AccountDropZone,
};
